
import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getBaseUrl } from "@/utils/environment";

export const DebugDocTab = () => {
  const [debugDoc, setDebugDoc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDebugDoc() {
      try {
        // Use the baseUrl to correctly handle different environments
        const baseUrl = getBaseUrl();
        // Try multiple potential paths to find the DEBUG.md file
        const potentialPaths = [
          `${baseUrl}docs/DEBUG.md`,
          '/docs/DEBUG.md',
          './docs/DEBUG.md',
          '../docs/DEBUG.md'
        ];
        
        console.log('DEBUG.md: Attempting to load from paths:', potentialPaths);
        
        // Try each path until we find one that works
        let response = null;
        let successful = false;
        
        for (const path of potentialPaths) {
          try {
            console.log(`DEBUG.md: Trying path ${path}`);
            const tempResponse = await fetch(path, { 
              cache: 'no-store',  // Prevent caching issues
              headers: { 'Cache-Control': 'no-cache' }
            });
            
            if (tempResponse.ok) {
              console.log(`DEBUG.md: Successfully loaded from ${path}`);
              response = tempResponse;
              successful = true;
              break;
            } else {
              console.log(`DEBUG.md: Failed to load from ${path} (Status: ${tempResponse.status})`);
            }
          } catch (pathErr) {
            console.log(`DEBUG.md: Error trying path ${path}:`, pathErr);
            // Continue to the next path
          }
        }
        
        if (!successful || !response) {
          throw new Error(`Failed to load DEBUG.md from any path`);
        }
        
        const text = await response.text();
        setDebugDoc(text);
      } catch (err) {
        console.error("Error loading DEBUG.md:", err);
        setError(err instanceof Error ? err.message : 'Failed to load debug documentation');
      } finally {
        setIsLoading(false);
      }
    }

    fetchDebugDoc();
  }, []);

  // Simple Markdown rendering for code blocks and headers
  const renderMarkdown = (markdown: string) => {
    // Process code blocks
    let html = markdown.replace(/```(.+?)\n([\s\S]+?)```/g, '<pre class="bg-gray-100 p-4 rounded my-4 overflow-auto"><code>$2</code></pre>');
    
    // Process headers
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-5 mb-3">$1</h2>');
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>');
    
    // Process lists
    html = html.replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>');
    
    // Process paragraphs
    html = html.replace(/^([^<\n].+)$/gm, '<p class="my-2">$1</p>');
    
    // Remove empty paragraphs
    html = html.replace(/<p class="my-2"><\/p>/g, '');
    
    // Process consecutive list items
    html = html.replace(/(<li[^>]*>.*<\/li>\n)<li/g, '$1<li');
    html = html.replace(/(<li[^>]*>.*<\/li>\n)(?!<li)/g, '<ul>$1</ul>\n');
    
    return html;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error Loading Documentation</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6 prose max-w-none">
        {debugDoc && (
          <div 
            className="debug-doc-content" 
            dangerouslySetInnerHTML={{ __html: renderMarkdown(debugDoc) }} 
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DebugDocTab;
